import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { MovementManufacturer } from '../../types';
import {
  createMovementManufacturer,
  deriveManufacturerSlug,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { CountrySelect } from '../common/CountrySelect';

interface MovementManufacturerQuickCreateProps {
  isOpen: boolean;
  /** Optional pre-fill — used when invoked from a typed query in the parent picker. */
  initialName?: string;
  onCancel: () => void;
  /** Fires with the new doc id + manufacturer record. Parent decides what to do. */
  onCreated: (id: string, manufacturer: MovementManufacturer) => void;
}

/**
 * Slim "+ Create new manufacturer" modal. Captures only the fields the calibre
 * editor needs to immediately wire up a new manufacturer (name, country,
 * isGeneric). Slug derives from name. Other fields — descriptions, parent,
 * AI enrichment — are filled in later on the full MovementManufacturerDetail.
 *
 * Exported standalone so other surfaces can reuse it; only the calibre editor
 * wires it up for now.
 */
export function MovementManufacturerQuickCreate({
  isOpen,
  initialName,
  onCancel,
  onCreated,
}: MovementManufacturerQuickCreateProps) {
  const [name, setName] = useState(initialName ?? '');
  const [country, setCountry] = useState('');
  const [isGeneric, setIsGeneric] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slug = useMemo(() => deriveManufacturerSlug(name), [name]);
  const canSave = !!slug && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const trimmed = name.trim();
      const created = await createMovementManufacturer({
        name: trimmed,
        displayName: trimmed,
        country: country.trim() || undefined,
        // Explicit boolean — `isGeneric || undefined` would strip a `false`,
        // leaving the field absent (see MovementManufacturerDetail save note).
        isGeneric: Boolean(isGeneric),
      });
      const record: MovementManufacturer = {
        id: created,
        name: trimmed,
        displayName: trimmed,
        country: country.trim() || undefined,
        isGeneric: Boolean(isGeneric),
      } as MovementManufacturer;
      toast.success(`Created "${trimmed}"`);
      onCreated(created, record);
      // Reset for next open.
      setName('');
      setCountry('');
      setIsGeneric(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!saving) onCancel();
      }}
      title="Create movement manufacturer"
      size="md"
    >
      <div className="space-y-3 text-sm">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g., "Sellita"'
            disabled={saving}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
            autoFocus
          />
          {slug && (
            <div className="mt-1 text-xs text-gray-500">
              Doc ID: <code className="font-mono">{slug}</code>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Country
          </label>
          <CountrySelect
            value={country}
            onChange={(v) => setCountry(v)}
          />
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isGeneric}
            onChange={(e) => setIsGeneric(e.target.checked)}
            disabled={saving}
            className="rounded text-brand-600 focus:ring-brand-500"
          />
          Generic (ETA / Sellita / Miyota style — unscoped from any single brand)
        </label>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSave} disabled={!canSave} loading={saving}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
